import React, { FC, memo, useCallback, useContext, useEffect, useState, useMemo } from 'react';

import PdfViewerModal from '../../../../components/Pdfs';
import { getDataOptions } from '../../../../utils/arrayUtils';
import { documentTypesEnum } from '../../../../api/vouchers/helpers';
import { documentTypeToKey } from '../../../../api/emailTemplates/documentKeys';
import { buildMergeContext } from '../../../../utils/buildMergeContext';
import { useTravelFileTourName } from '../../../../hooks/useTravelFileTourName';
import { getDefaultRecipientEmails } from '../../../../utils/getDefaultRecipientEmails';
import VoucherServicePdfDocument from '../../../../components/Pdfs/Vouchers/VoucherServicePdfDocument';
import RefundServicePdfDocument from '../../../../components/Pdfs/Vouchers/RefundServicePdfDocument';
import { Voucher } from '../../../../api/vouchers/types';
import { Context as VouchersContext } from '../../../../api/vouchers/context';
import { displayToast } from '../../../../components/RTToast';
import { getVoucherEmailLogBody, logEmailSent, sendFilesToEmails } from '../../../../components/Pdfs/sendFilesToEmails';
import { Context as ClientsContext } from '../../../../api/clients/context';
import { Context as TravelFilesContext } from '../../../../api/travelFiles/context';
import { Context as AuthContext } from '../../../../api/auth/context';
import { Context as TravelAgencyContext } from '../../../../api/travelAgencyAccounts/context';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ReceiptPdf from '../../../../components/Pdfs/Vouchers/ReceiptPdf';
import ReceiptPdfHe from '../../../../components/Pdfs/Vouchers/ReceiptPdfHe';
import vouchersApi from '../../../../api/vouchers/api';
import InvoicePdf from '../../../../components/Pdfs/Vouchers/InvoicePdf';
import { travelFileTypesEnum } from '../../../../api/travelFiles/constants/travelFileTypesOptions';
import { isImportedId } from '../../../../api/services/constants/importedRecords';
import { useTravelBoosterRetired } from '../../../../permissions';
import { Context as CurrenciesContext } from '../../../../api/currencies/context';
import RemittancePdf from '../../../../components/Pdfs/Vouchers/RemittancePdf';
import TaxInvoicePdf from '../../../../components/Pdfs/Vouchers/TaxInvoicePdf';
import AgentInvoicePdf from '../../../../components/Pdfs/Vouchers/AgentInvoicePdf';
import CreditDebitPdf from '../../../../components/Pdfs/Vouchers/CreditDebitPdf';
import PRQPdf from '../../../../components/Pdfs/Vouchers/PRQPdf';
import DepositPdf from '../../../../components/Pdfs/Vouchers/DepositPdf';
import WithdrawalPdf from '../../../../components/Pdfs/Vouchers/WithdrawalPdf';
import StatementPdf from '../../../../components/Pdfs/Vouchers/StatementPdf';
import { Context as SupplierAccountsContext } from '../../../../api/supplierAccounts/context';
import { Context as BankAccountsContext } from '../../../../api/bankAccounts/context';
import { usePdfInfoBuilder } from '../../../../hooks/usePdfInfoBuilder';

type Props = {
    toggleVoucherModalOpen: (bool?: boolean) => void;
    voucherProps: Voucher; // not always with info
    sendEmails?: boolean;
    extraActions?: React.ReactElement | null;
};
const ViewVoucherPdf: FC<Props> = (props: Props) => {
    const { toggleVoucherModalOpen, voucherProps, sendEmails, extraActions } = props;

    const vouchersContext = useContext(VouchersContext);
    const [voucher, setVoucher] = useState<Voucher | null>(null);
    const [coveredDocuments, setCoveredDocuments] = useState<Voucher[]>([]);
    const [serviceData, setServiceData] = useState<any>(null);
    const travelBoosterRetired = useTravelBoosterRetired();
    const [fetchedTravelAgencyAccount, setFetchedTravelAgencyAccount] = useState<any>(null);
    const [fetchedSupplierAccount, setFetchedSupplierAccount] = useState<any>(null);
    const [fetchedBankAccount, setFetchedBankAccount] = useState<any>(null);

    // A receipt prints in English or Hebrew. It opens in the language the customer
    // paid in when they came through tour registration, English otherwise, and
    // staff can switch. The Email button attaches whichever is showing.
    const isReceipt =
        voucherProps.type === documentTypesEnum.RECEIPT || voucherProps.type === documentTypesEnum.TRANSFER_RECEIPT;
    const [receiptLanguage, setReceiptLanguage] = useState<'en' | 'he'>('en');
    useEffect(() => {
        if (!isReceipt || !voucherProps._id) return;
        vouchersApi
            .getReceiptLanguage(String(voucherProps._id))
            .then((res: any) => {
                if (res?.language === 'he') setReceiptLanguage('he');
            })
            .catch(() => undefined);
    }, [isReceipt, voucherProps._id]);

    const travelFilesContext = useContext(TravelFilesContext);
    const authContext = useContext(AuthContext);
    const clientsContext = useContext(ClientsContext);
    const travelAgencyContext = useContext(TravelAgencyContext);
    const supplierAccountsContext = useContext(SupplierAccountsContext);
    const bankAccountsContext = useContext(BankAccountsContext);
    const currenciesContext = useContext(CurrenciesContext);

    // Hook to build pdfInfo from stored data when not available
    const { getPdfInfo, isReady: isPdfInfoBuilderReady } = usePdfInfoBuilder();

    useEffect(() => {
        // Always fetch from server to get complete data (including description field)
        // Use getById which returns info as object (not array like getOne)
        if (voucherProps._id) {
            vouchersContext
                .getById(voucherProps._id)
                .then(async (serverVoucher) => {
                    // Preserve pdfInfo from props — non-service docs (Invoice, Receipt, etc.)
                    // build pdfInfo at creation time but it's not stored in DB
                    const propsPdfInfo = (voucherProps as any)?.info?.pdfInfo;
                    const propsHidePassengers = (voucherProps as any)?.info?.hidePassengersTable;
                    if (propsPdfInfo && !serverVoucher?.info?.pdfInfo) {
                        serverVoucher = {
                            ...serverVoucher,
                            info: { ...serverVoucher.info, pdfInfo: propsPdfInfo }
                        };
                    }
                    // The column is persisted now, so the server value wins; fall
                    // back to props only when the server has no opinion (legacy
                    // rows, or a doc issued before the column existed). `false`
                    // is a real choice — compare against undefined, not truthiness.
                    if (
                        propsHidePassengers !== undefined &&
                        (serverVoucher?.info as any)?.hidePassengersTable === undefined
                    ) {
                        serverVoucher = {
                            ...serverVoucher,
                            info: { ...serverVoucher.info, hidePassengersTable: propsHidePassengers }
                        };
                    }
                    // Preserve approval data from props (approverName/signatureData come from query join, not getById)
                    if ((voucherProps as any).approverName || (voucherProps as any).approverSignatureData) {
                        serverVoucher = {
                            ...serverVoucher,
                            isApproved: serverVoucher.isApproved ?? (voucherProps as any).isApproved,
                            approverName: serverVoucher.approverName || (voucherProps as any).approverName,
                            approvalDate: serverVoucher.approvalDate || (voucherProps as any).approvalDate,
                            approverSignatureData: (serverVoucher as any).approverSignatureData || (voucherProps as any).approverSignatureData
                        };
                    }
                    setVoucher(serverVoucher);

                    // Every dependent fetch below is independent — covered
                    // documents, service data and the three account reads all need
                    // only serverVoucher, which is already in hand. Previously they
                    // ran in three sequential waves (covered → service → accounts),
                    // costing extra browser → server round trips before the PDF
                    // could render — most of the visible delay on Pegasus. They now
                    // all fire in one parallel wave. Each keeps its own catch so one
                    // failing never denies the PDF the others.
                    const dependentFetches: Array<Promise<unknown>> = [];

                    // For Deposit/Withdrawal/Statement/Remittance/PRQ, fetch covered documents
                    const coveredDocTypes = [documentTypesEnum.DEPOSIT, documentTypesEnum.WITHDRAWAL, documentTypesEnum.STATEMENT, documentTypesEnum.REMITTANCE, documentTypesEnum.PRQ, documentTypesEnum.AGENT_INVOICE, documentTypesEnum.TRANSFER_RECEIPT, documentTypesEnum.RECEIPT];
                    if (coveredDocTypes.includes(serverVoucher.type)) {
                        const isCoveringIds = (serverVoucher.info as any)?.isCovering || [];
                        if (isCoveringIds.length > 0) {
                            dependentFetches.push(
                                Promise.all(
                                    isCoveringIds.map((id: string | number) => vouchersContext.getById(id))
                                )
                                    .then((coveredDocs) => setCoveredDocuments(coveredDocs.filter(Boolean)))
                                    .catch((err) =>
                                        console.error('Error fetching covered documents:', err)
                                    )
                            );
                        }
                    }

                    // For Voucher/Refund, fetch full service data for PDF building
                    const serviceVoucherTypes = [
                        documentTypesEnum.VOUCHER,
                        documentTypesEnum.REFUND,
                        documentTypesEnum.EXTENDED_REFUND
                    ];
                    if (serviceVoucherTypes.includes(serverVoucher.type) && serverVoucher.travelFileServiceId) {
                        dependentFetches.push(
                            travelFilesContext
                                .getFullService(serverVoucher.travelFileServiceId)
                                .then((fullService) => {
                                    if (fullService) setServiceData(fullService);
                                })
                                .catch((err) =>
                                    console.error('Error fetching service data for voucher PDF:', err)
                                )
                        );
                    }

                    const agentId = serverVoucher.targetType === 'agent' ? serverVoucher.targetId
                        : serverVoucher.sourceType === 'agent' ? serverVoucher.sourceId
                        : null;

                    let supplierId = serverVoucher.sourceType === 'supplier' ? serverVoucher.sourceId
                        : serverVoucher.targetType === 'supplier' ? serverVoucher.targetId
                        : null;
                    // For TRC/Receipt: source is 'account', not 'supplier' — resolve by name
                    if (!supplierId && (serverVoucher.info as any)?.supplierName) {
                        const supplierName = (serverVoucher.info as any).supplierName;
                        const found = getDataOptions(supplierAccountsContext.data).find(
                            (s: any) => s.supplierCompanyName === supplierName
                        );
                        if (found) supplierId = found._id;
                    }

                    // 'account' sourceType/targetType can be bank, income, general, discount,
                    // or agent commission accounts. Only bank accounts exist in bankAccounts —
                    // a 404 for the others sets a placeholder so isAllDataReady still proceeds.
                    const bankAccountId = serverVoucher.sourceType === 'account' ? serverVoucher.sourceId
                        : serverVoucher.targetType === 'account' ? serverVoucher.targetId
                        : null;

                    if (agentId) {
                        dependentFetches.push(
                            travelAgencyContext
                                .getById(String(agentId))
                                .then((account: any) => {
                                    if (account) setFetchedTravelAgencyAccount(account);
                                })
                                .catch((err: any) =>
                                    console.error('Error fetching travel agency account for PDF:', err)
                                )
                        );
                    }
                    if (supplierId) {
                        dependentFetches.push(
                            supplierAccountsContext
                                .getById(String(supplierId))
                                .then((account: any) => {
                                    if (account) setFetchedSupplierAccount(account);
                                })
                                .catch((err: any) =>
                                    console.error('Error fetching supplier account for PDF:', err)
                                )
                        );
                    }
                    if (bankAccountId) {
                        dependentFetches.push(
                            bankAccountsContext
                                .getById(String(bankAccountId))
                                .then((account: any) => setFetchedBankAccount(account || ({} as any)))
                                .catch((err: any) => {
                                    console.error('Error fetching bank account for PDF:', err);
                                    setFetchedBankAccount({} as any);
                                })
                        );
                    }

                    await Promise.all(dependentFetches);
                })
                .catch((err) => {
                    displayToast('error', 'Error while getting voucher', 'Something went wrong');
                    // Fallback to props data if fetch fails
                    if (voucherProps.info) {
                        setVoucher(voucherProps);
                    }
                    throw err;
                });
        } else if (voucherProps.info) {
            setVoucher(voucherProps);
        }
    }, []); // eslint-disable-line

    // Wait until all required async data has arrived before building pdfInfo.
    // This prevents BlobProvider from re-rendering on every intermediate state change.
    const isAllDataReady = useMemo(() => {
        if (!voucher) return false;
        if (!isPdfInfoBuilderReady) return false;

        const serviceVoucherTypes = [documentTypesEnum.VOUCHER, documentTypesEnum.REFUND, documentTypesEnum.EXTENDED_REFUND];
        const isServiceVoucher = serviceVoucherTypes.includes(voucher.type as any);

        // Service voucher needs serviceData
        if (isServiceVoucher && voucher.travelFileServiceId && !serviceData) return false;

        // Statement/Remittance/PRQ/Deposit/Withdrawal need covered documents
        const coveredDocTypes = [documentTypesEnum.STATEMENT, documentTypesEnum.REMITTANCE, documentTypesEnum.PRQ, documentTypesEnum.DEPOSIT, documentTypesEnum.WITHDRAWAL, documentTypesEnum.TRANSFER_RECEIPT, documentTypesEnum.RECEIPT];
        if (coveredDocTypes.includes(voucher.type as any) && (voucher.info as any)?.isCovering?.length > 0 && coveredDocuments.length === 0) return false;

        // Wait for bank account if document references one with an ID
        const bankId = voucher.sourceType === 'account' ? voucher.sourceId : voucher.targetType === 'account' ? voucher.targetId : null;
        if (bankId && !fetchedBankAccount) return false;

        // Wait for supplier if document references one with an ID
        const supplierId = voucher.sourceType === 'supplier' ? voucher.sourceId : voucher.targetType === 'supplier' ? voucher.targetId : null;
        if (supplierId && !fetchedSupplierAccount) return false;

        // Wait for agent if document references one with an ID
        const agentId = voucher.sourceType === 'agent' ? voucher.sourceId : voucher.targetType === 'agent' ? voucher.targetId : null;
        if (agentId && !fetchedTravelAgencyAccount) return false;

        return true;
    }, [voucher, isPdfInfoBuilderReady, serviceData, fetchedTravelAgencyAccount, coveredDocuments, fetchedBankAccount, fetchedSupplierAccount]);

    // Build voucher with pdfInfo from stored data when not available
    const voucherWithPdfInfo = useMemo(() => {
        if (!voucher || !isAllDataReady) return null;

        // Normalize info - findOne returns array, we need object
        let normalizedVoucher = voucher;
        if (Array.isArray(voucher.info)) {
            normalizedVoucher = { ...voucher, info: voucher.info[0] || {} } as Voucher;
        }

        // Helper to populate currency if it's just an ID
        const populateCurrency = (info: any) => {
            if (!info?.currency) return info;
            if (typeof info.currency === 'object' && info.currency?.code) return info;
            const currencyId = typeof info.currency !== 'object' ? info.currency : info.currency?._id;
            if (currencyId && currenciesContext.dataAsArray?.length) {
                const currencyObj = currenciesContext.dataAsArray.find((c) => c._id === currencyId);
                if (currencyObj) return { ...info, currency: currencyObj };
            }
            return info;
        };

        const overrides: any = {};
        if (fetchedTravelAgencyAccount) overrides.travelAgencyAccount = fetchedTravelAgencyAccount;
        if (fetchedSupplierAccount) overrides.supplierAccount = fetchedSupplierAccount;
        if (fetchedBankAccount) overrides.bankAccount = fetchedBankAccount;
        const builtPdfInfo = getPdfInfo(normalizedVoucher, coveredDocuments, serviceData, Object.keys(overrides).length ? overrides : undefined);
        if (builtPdfInfo) {
            const populatedInfo = populateCurrency(normalizedVoucher.info);
            // No hidePassengersTable override here — buildServiceVoucher already
            // resolves info.hidePassengersTable against the per-type default, and
            // forcing `true` here used to discard the user's opt-in.
            // Inject approval data for PRQ PDF signature section
            if (normalizedVoucher.isApproved && normalizedVoucher.approverName) {
                builtPdfInfo.approval = {
                    approverName: normalizedVoucher.approverName,
                    approvalDate: normalizedVoucher.approvalDate,
                    signatureData: (normalizedVoucher as any).approverSignatureData || null
                };
            }
            return {
                ...normalizedVoucher,
                info: {
                    ...populatedInfo,
                    ...(builtPdfInfo.supplierNameOverride ? { supplierName: builtPdfInfo.supplierNameOverride } : {}),
                    // Merge travelFileNumber from builder if not on info (e.g. Withdrawal resolves from covered receipt)
                    ...(builtPdfInfo.travelFileNumber && !populatedInfo?.travelFileNumber ? { travelFileNumber: builtPdfInfo.travelFileNumber } : {}),
                    pdfInfo: builtPdfInfo
                }
            } as Voucher;
        }

        // Fallback: use stored pdfInfo if dynamic builder returned incomplete data
        const existingPdfInfo = (normalizedVoucher.info as any)?.pdfInfo;
        if (existingPdfInfo && Object.keys(existingPdfInfo).length > 1) {
            const populatedInfo = populateCurrency(normalizedVoucher.info);
            if (populatedInfo !== normalizedVoucher.info) {
                return { ...normalizedVoucher, info: populatedInfo } as Voucher;
            }
        }

        return normalizedVoucher;
    }, [isAllDataReady, voucher, getPdfInfo, currenciesContext.dataAsArray, coveredDocuments, serviceData, fetchedTravelAgencyAccount]);

    useEffect(() => {
        if (sendEmails && voucher) {
            const emails = voucher.info?.pdfInfo?.emails;
            sendFilesToEmails({
                emailBody: getVoucherEmailLogBody({
                    voucherType: voucherProps.type,
                    voucherNumber: voucherProps.number,
                    emails
                }),
                title: voucherProps.type,
                emails,
                travelFilesContext,
                user: authContext.currUser?._id || ''
            });
        }
    }, [voucher]); // eslint-disable-line

    const pdfTitle = (() => {
        const docType = voucher?.type || voucherProps.type;
        const docNumber = voucher?.number ?? voucherProps.number ?? '';

        const baseTitle = (() => {
            switch (docType) {
                case documentTypesEnum.RECEIPT:
                case documentTypesEnum.TRANSFER_RECEIPT: {
                    // Name it by what it IS. Re-deriving from the payment method
                    // renames the receipts issued before the current rule — a
                    // card payment stored as 'Transfer Receipt' would download
                    // as "Receipt", contradicting its type and number.
                    return `${docType || ''} ${docNumber}`;
                }
                default: {
                    return `${docType} No. ${docNumber}`;
                }
            }
        })();

        switch (docType) {
            case documentTypesEnum.REMITTANCE:
            case documentTypesEnum.INVOICE:
            case documentTypesEnum.TAX_INVOICE:
            case documentTypesEnum.RECEIPT:
            case documentTypesEnum.TRANSFER_RECEIPT: {
                const groupText = `${
                    voucherProps.travelFileType === travelFileTypesEnum.GROUP ? 'Group ' : ''
                }`;
                return `${groupText}${baseTitle}`;
            }
            default: {
                return baseTitle;
            }
        }
    })();
    const pdfName = (() => {
        switch (voucher?.type) {
            case documentTypesEnum.RECEIPT:
            case documentTypesEnum.TRANSFER_RECEIPT: {
                return `${pdfTitle} (${voucher.info?.paymentMethodType || ''})`;
            }
            default: {
                return pdfTitle;
            }
        }
    })();

    // Runs after the send succeeds — the modal owns sending; this records what went out.
    // The template name is part of the entry so the log says which message was used, not
    // merely that something was emailed.
    const handleEmailsSent = useCallback(
        (emails: string[], meta: { templateName?: string }) => {
            const base = getVoucherEmailLogBody({
                voucherType: voucherProps.type,
                voucherNumber: voucherProps.number,
                emails
            });
            logEmailSent({
                travelFilesContext,
                user: authContext.currUser?._id || '',
                body: meta.templateName ? `${base} (message: ${meta.templateName})` : base
            });
        },
        [authContext.currUser, travelFilesContext, voucherProps.number, voucherProps.type]
    );

    // The document's own vocabulary, so the compose dialog can find the messages the
    // client configured for this document type.
    const emailDocumentKey = documentTypeToKey(voucher?.type || voucherProps.type);
    const emailDefaultRecipients = useMemo(
        () => getDefaultRecipientEmails(travelFilesContext.selected, emailDocumentKey),
        [travelFilesContext.selected, emailDocumentKey]
    );

    const emailTourName = useTravelFileTourName(travelFilesContext.selected);

    const emailMergeContext = useMemo(
        () =>
            buildMergeContext({
                document: {
                    type: voucher?.type || voucherProps.type,
                    number: voucher?.number ?? voucherProps.number,
                    info: (voucher?.info || voucherProps.info) as any
                },
                travelFile: travelFilesContext.selected
                    ? ({ ...travelFilesContext.selected, tourName: emailTourName } as any)
                    : null,
                client: clientsContext.selected as any
            }),
        [voucher, voucherProps, travelFilesContext.selected, emailTourName, clientsContext.selected]
    );

    const emailContextHeader = (() => {
        const tfNumber =
            (voucher?.info as any)?.travelFileNumber ||
            (travelFilesContext.selected as any)?.travelFileNumber;
        const passenger = emailMergeContext['First Customer Name'];
        return [tfNumber ? `#${tfNumber}` : '', passenger, pdfTitle].filter(Boolean).join(' · ');
    })();

    const pdfVoucher = voucherWithPdfInfo;
    const hasPdfInfo = pdfVoucher?.info?.pdfInfo && Object.keys(pdfVoucher.info.pdfInfo).length > 1;

    const serviceVoucherTypes = [
        documentTypesEnum.VOUCHER,
        documentTypesEnum.REFUND,
        documentTypesEnum.EXTENDED_REFUND
    ];
    const isServiceVoucher = serviceVoucherTypes.includes(pdfVoucher?.type as any);

    // Service voucher is missing pdfInfo only if there's no travelFileServiceId (old data)
    const serviceVoucherMissingPdfInfo = isServiceVoucher && !hasPdfInfo && isAllDataReady;

    const isOtherDocType = pdfVoucher?.type && !isServiceVoucher;
    const otherDocMissingPdfInfo = isOtherDocType && !hasPdfInfo && isAllDataReady;

    const existingPdfUrl = Array.isArray(voucherProps.info)
        ? voucherProps.info[0]?.pdfUrl
        : voucherProps.info?.pdfUrl;
    // The server now renders + uploads every supported document to S3 on
    // creation (generateAndUploadPdf). The client should ONLY auto-upload the
    // types the server SKIPS — service vouchers / refunds, which need travelFile
    // data and can't be rendered server-side. Otherwise both paths upload and
    // S3 ends up with two copies of the same document.
    // Uploading the rendered PDF records its S3 key with
    //   UPDATE documentinfo SET "pdfUrl" = ... WHERE "documentId" = $1
    // and for an imported document the read-only trigger refuses that. The key
    // is therefore never stored, getPdfUrl can never hit, and every open
    // re-renders and re-uploads the same file — forever, for all 622,035
    // imported documents (0 of which currently hold a pdfUrl). On one machine
    // that is invisible; on Pegasus it is a PDF pushed up from the browser and
    // out to S3 on every single open, to be discarded.
    //
    // Gated on the retirement flag rather than the id alone: once TravelBooster
    // is retired and its rows are folded into app.*, the UPDATE succeeds and S3
    // caching resumes here with no code change and nothing to remember.
    const importedAndReadOnly = isImportedId(voucherProps._id) && !travelBoosterRetired;
    const shouldAutoUpload = !existingPdfUrl && isServiceVoucher && !importedAndReadOnly;
    // A receipt is always rendered here, never shown from its stored S3 copy: the
    // stored copy is frozen at issue time — in the customer's language and with the
    // table layout of that day — so it could neither switch language nor pick up a
    // corrected layout. The S3 copy stays as the original record.
    const showStoredPdf = !!existingPdfUrl && !isReceipt;

    const languageToggle = isReceipt ? (
        <ToggleButtonGroup
            size="small"
            exclusive
            value={receiptLanguage}
            onChange={(_e, value) => value && setReceiptLanguage(value)}
        >
            <ToggleButton value="en">English</ToggleButton>
            <ToggleButton value="he">עברית</ToggleButton>
        </ToggleButtonGroup>
    ) : null;

    return (
        <PdfViewerModal
            open
            toggleDialog={toggleVoucherModalOpen}
            title={pdfTitle}
            pdfName={pdfName}
            onEmailsSent={handleEmailsSent}
            documentKey={emailDocumentKey}
            emailContextHeader={emailContextHeader}
            mergeContext={emailMergeContext}
            defaultEmails={emailDefaultRecipients}
            extraActions={
                languageToggle ? (
                    <>
                        {languageToggle}
                        {extraActions}
                    </>
                ) : (
                    extraActions
                )
            }
            autoUploadDocumentId={shouldAutoUpload ? voucherProps._id : undefined}
            storedPdfDocumentId={showStoredPdf ? voucherProps._id : undefined}
        >
            {(() => {
                if (!pdfVoucher) {
                    return null;
                }

                if (serviceVoucherMissingPdfInfo) {
                    return (
                        <div style={{ padding: 40, textAlign: 'center' }}>
                            <p>PDF preview not available for this document.</p>
                            <p style={{ color: '#666', fontSize: 14 }}>
                                Service Vouchers created before migration require the original PDF data.
                            </p>
                        </div>
                    );
                }

                // Show message for other document types where pdfInfo couldn't be built
                if (otherDocMissingPdfInfo) {
                    return (
                        <div style={{ padding: 40, textAlign: 'center' }}>
                            <p>Unable to generate PDF preview.</p>
                            <p style={{ color: '#666', fontSize: 14 }}>
                                Required data for PDF generation is not available.
                            </p>
                        </div>
                    );
                }

                switch (pdfVoucher?.type) {
                    case documentTypesEnum.VOUCHER:
                        return <VoucherServicePdfDocument {...pdfVoucher} />;
                    case documentTypesEnum.REFUND:
                    case documentTypesEnum.EXTENDED_REFUND:
                        return <RefundServicePdfDocument {...pdfVoucher} />;
                    case documentTypesEnum.RECEIPT:
                    case documentTypesEnum.TRANSFER_RECEIPT:
                        return receiptLanguage === 'he' ? (
                            <ReceiptPdfHe {...pdfVoucher} />
                        ) : (
                            <ReceiptPdf {...pdfVoucher} />
                        );
                    case documentTypesEnum.INVOICE:
                        return <InvoicePdf {...pdfVoucher} />;
                    case documentTypesEnum.TAX_INVOICE:
                        return <TaxInvoicePdf {...pdfVoucher} />;
                    case documentTypesEnum.AGENT_INVOICE:
                        return <AgentInvoicePdf {...pdfVoucher} />;
                    case documentTypesEnum.CREDIT_DEBIT:
                    case documentTypesEnum.DEBIT_CREDIT:
                        return <CreditDebitPdf {...pdfVoucher} />;
                    case documentTypesEnum.REMITTANCE:
                        return <RemittancePdf {...pdfVoucher} />;
                    case documentTypesEnum.PRQ:
                        return <PRQPdf {...pdfVoucher} />;
                    case documentTypesEnum.DEPOSIT:
                        return <DepositPdf {...pdfVoucher} />;
                    case documentTypesEnum.WITHDRAWAL:
                        return <WithdrawalPdf {...pdfVoucher} />;
                    case documentTypesEnum.STATEMENT:
                        return <StatementPdf {...pdfVoucher} />;
                    default:
                        return null;
                }
            })()}
        </PdfViewerModal>
    );
};

export default memo(ViewVoucherPdf);
